import { AxiosError, isAxiosError } from 'axios';
import { isWithinInterval, subDays } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IConfig } from '../../config';
import {
  CIQOHLC,
  EStreamChartDTO,
  EStreamChartEntry,
  QuoteFeedCallback,
  QuoteFeedInitialDataParams,
  QuoteFeedInterval,
} from '../../types';
import { getFormatter, NbLogger, parseDateFromEStreamChart } from '../../utils';

const mapEStreamChartEntryToChartIq = (item: EStreamChartEntry): CIQOHLC => {
  return {
    Date: undefined,
    DT: parseDateFromEStreamChart(item.T),
    Open: item.O,
    High: item.H,
    Low: item.L,
    Close: item.C,
    Volume: item.V,
  };
};

type UseChartCommonInput = {
  config: IConfig;
  getChartData: (
    ric: string,
    interval: QuoteFeedInterval,
    from: Date,
    to: Date,
    abortController?: AbortController,
  ) => Promise<EStreamChartDTO>;
  handleError: (error: any) => void;
  logger: NbLogger;
};

export function useChartCommon(input: UseChartCommonInput) {
  const { config, logger, getChartData, handleError } = input;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChartLoadingFailed, setIsChartLoadingFailed] = useState(false);
  const lastPaginationData = useRef<{ symbol: string; startDate: Date; endDate: Date } | null>(null);
  const { formatDateTimeShort } = getFormatter();
  const chartDataGetterControllers = useRef<Record<string, AbortController>>({});

  const abortInitiatedGetters = useCallback(() => {
    Object.values(chartDataGetterControllers.current).forEach(c => c.abort());
  }, []);

  /**
   * Loads chart data from eStream
   * @param ric RIC of the instrument
   * @param startDate Start date of the chart
   * @param endDate End date of the chart
   * @param interval Interval of the chart (daily or minute/intraday)
   * @param stx if the chart is displayed in a chartiq chart, this is the chart object so we can update the chart values with latest estream data
   */
  const loadChartDataByRic = useCallback(
    async (
      ric: string,
      startDate: Date,
      endDate: Date,
      interval: QuoteFeedInterval,
    ): Promise<{ quotes: Array<CIQOHLC>; minStartDate?: Date; hasError?: true }> => {
      setIsLoading(true);
      setIsChartLoadingFailed(false);
      const loadId = `loadChart(${interval}): ${ric} / ${formatDateTimeShort(startDate)} ${formatDateTimeShort(
        endDate,
      )}`;
      try {
        logger.debug(loadId);
        const controller = new AbortController();
        chartDataGetterControllers.current = { ...chartDataGetterControllers.current, [loadId]: controller };
        const chartData = await getChartData(ric, interval, startDate, endDate, controller);

        if (chartData.data.length > 0 && !chartData.data[0].T) {
          // if the first entry doesn't have a timestamp, it's invalid
          throw new Error();
        }

        logger.debug(
          ` => ${ric} - ${chartData.data.length} entries ${
            chartData.data.length > 0
              ? `[${chartData.data[0].T} <-> ${chartData.data[chartData.data.length - 1].T}]`
              : ``
          }`,
        );
        const quotes = chartData.data.map(mapEStreamChartEntryToChartIq);
        return {
          quotes,
          minStartDate: chartData.meta ? new Date((chartData.meta as any)[0].min_starttime) : undefined,
        };
      } catch (err) {
        // If the getter has been aborted then skip error throw
        if (isAxiosError(err) && err.code === AxiosError.ERR_CANCELED) {
          return { quotes: [] };
        }
        setIsChartLoadingFailed(true);
        logger.error(`Failed to get chart data`, err);
        handleError(err);
        return { quotes: [], hasError: true };
      } finally {
        delete chartDataGetterControllers.current[loadId];
        setIsLoading(false);
      }
    },
    [getChartData, formatDateTimeShort, handleError],
  );

  const loadChartDataByRicEventually = useCallback(
    async (
      ric: string,
      suggestedStartDate: Date,
      endDate: Date,
      interval: QuoteFeedInterval,
    ): Promise<{
      quotes: Array<CIQOHLC>;
      minStartDate?: Date;
      hasError?: true;
    }> => {
      let tries = 0;
      let startDate = suggestedStartDate;
      while (tries < config.Chart.MaxChartQueryRetry) {
        const data = await loadChartDataByRic(ric, startDate, endDate, interval);
        if (data.hasError) {
          tries = config.Chart.MaxChartQueryRetry;
        } else {
          if (data.quotes.length > 0) {
            return data;
          }
          startDate = subDays(startDate, 1);
          tries++;
        }
      }
      return {
        quotes: [],
        hasError: true,
      };
    },
    [loadChartDataByRic],
  );

  const createQuoteFeed = useCallback(() => {
    return {
      async fetchInitialData(
        _symbol: string,
        suggestedStartDate: Date,
        suggestedEndDate: Date,
        params: QuoteFeedInitialDataParams,
        cb: QuoteFeedCallback,
      ) {
        lastPaginationData.current = null;
        const startDate = suggestedStartDate;
        const endDate = suggestedEndDate;
        // we need
        const chartData = await loadChartDataByRicEventually(
          params.symbolObject.ric,
          startDate,
          endDate,
          params.interval,
        );
        if (!chartData.hasError) {
          const moreAvailable =
            chartData.minStartDate &&
            !isWithinInterval(chartData.minStartDate, {
              start: startDate,
              end: endDate,
            });
          const res = { quotes: chartData.quotes, upToDate: chartData.quotes.length > 0, moreAvailable };
          cb(res);
        } else {
          cb({ quotes: [], upToDate: true, moreAvailable: false });
        }
      },
      async fetchPaginationData(
        _symbol: string,
        startDate: Date,
        endDate: Date,
        params: QuoteFeedInitialDataParams,
        cb: QuoteFeedCallback,
      ) {
        // There is an issue with chartiq pagination, because sometimes it keeps requesting for the same interval
        // This can happen when there is sparse data for a symbol, and the requested interval has almost zero data
        // For example: KULCSSOFT has so little data in day range with 1 hour interval we might have data for only 1 day
        // Returning with only 1 day data will cause chartiq to keep requesting the same day over and over again, it's
        // not able to understand that in this range we have no more data. To fix this we need to save the last pagination
        // so when ChartIQ requests the same data we can return in a special way with a DT object saying try again with this
        // date as the pagination's first date.
        if (
          lastPaginationData.current &&
          lastPaginationData.current.symbol === params.symbolObject.ric &&
          lastPaginationData.current.startDate.getTime() === startDate.getTime() &&
          lastPaginationData.current.endDate.getTime() === endDate.getTime()
        ) {
          // for month interval we go back 31 days, for day interval we go back 1 day
          if (params.interval === 'month') {
            const nextPaginationRequestDate = subDays(startDate, 31);
            cb([{ DT: nextPaginationRequestDate.toISOString() }]);
          } else {
            const nextPaginationRequestDate = subDays(startDate, 1);
            cb([{ DT: nextPaginationRequestDate.toISOString() }]);
          }
          return;
        }

        lastPaginationData.current = {
          symbol: params.symbolObject.ric,
          startDate,
          endDate,
        };
        const chartData = await loadChartDataByRic(params.symbolObject.ric, startDate, endDate, params.interval);
        if (!chartData.hasError) {
          const moreAvailable =
            chartData.minStartDate &&
            !isWithinInterval(chartData.minStartDate, {
              start: startDate,
              end: endDate,
            });
          const res = {
            quotes: chartData.quotes,
            upToDate: true,
            moreAvailable,
          };

          cb(res);
        } else {
          cb({ quotes: [], upToDate: true, moreAvailable: false });
        }
      },
    };
  }, [loadChartDataByRicEventually, loadChartDataByRic]);

  const clearErrors = useCallback(() => setIsChartLoadingFailed(false), [setIsChartLoadingFailed]);

  return useMemo(
    () => ({
      createQuoteFeed,
      loadChartDataByRic,
      isLoading,
      isChartLoadingFailed,
      clearErrors,
      abortInitiatedGetters,
    }),
    [createQuoteFeed, loadChartDataByRic, isLoading, isChartLoadingFailed, clearErrors, abortInitiatedGetters],
  );
}
